import { siteText } from '../siteText'
import { useEffect, useRef, type ReactNode } from 'react'
import './LiveWorkspaceLoading.css'

export interface LiveWorkspaceLoadingProgress {
  /** A normalized media-buffer measurement. Null means no finite total is known yet. */
  value: number | null
  /** Optional detail such as the buffered and target seconds. */
  bufferedSeconds?: number | null
  targetSeconds?: number | null
  /** Explains which real prerequisite is currently being measured. */
  label?: string
}

/** Keep the player and transcript mounted behind one startup screen. */
export function LiveWorkspaceLoading({ recording = false, progress, children }: { recording?: boolean; progress?: LiveWorkspaceLoadingProgress; children?: ReactNode }) {
  const dialog = useRef<HTMLElement>(null)
  useEffect(() => {
    const element = dialog.current
    const previousFocus = document.activeElement as HTMLElement | null
    element?.focus({ preventScroll: true })
    return () => {
      if (element?.contains(document.activeElement) && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true })
    }
  }, [])

  const measured = progress?.value != null && Number.isFinite(progress.value)
  const percent = measured ? Math.floor(Math.max(0, Math.min(1, progress!.value!)) * 100) : null
  const bufferedDetail = measured && progress?.bufferedSeconds != null && progress.targetSeconds != null
    ? `${progress.bufferedSeconds.toFixed(1)} of ${progress.targetSeconds.toFixed(1)} seconds buffered`
    : null
  const progressLabel = progress?.label || (recording ? 'Preparing recording' : 'Preparing live workspace')
  const progressDescription = bufferedDetail ? `${progressLabel} · ${bufferedDetail}` : progressLabel

  return <section ref={dialog} tabIndex={-1} className="live-workspace-loading" aria-label={siteText(recording ? 'Preparing Your Recording' : 'Preparing Your Live Workspace')}>
    <div className="live-startup-center">
      <div className="live-startup-art" aria-hidden="true">
        <span className="motion-logo workspace-loading-logo" />
      </div>
      <span className="sr-only" role="status">{siteText(recording ? 'Preparing your recording and transcript.' : 'Preparing your live workspace.')}</span>
      <div className={`live-startup-progress-wrap ${measured ? 'is-measured' : 'is-indeterminate'}`}>
        <div className="live-startup-progress" role="progressbar" aria-label={siteText(progressDescription)} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent ?? undefined} aria-valuetext={percent == null ? siteText('Waiting for a measurable loading signal') : siteText(`${percent}%`) }>
          <span style={percent == null ? undefined : { width: `${percent}%` }} />
        </div>
      </div>
      {children}
    </div>
  </section>
}
